import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { ArrowLeft, ArrowRight, Leaf, Loader2, CheckCircle, BookOpen, Sparkles, Target } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import "../styles/emergence.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmergenceParcoursDay() {
  const { parcoursId, dayNum } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [dayContent, setDayContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [journalingResponse, setJournalingResponse] = useState("");
  const [actionDone, setActionDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [userEmail, setUserEmail] = useState("");

  const isFrench = i18n.language === 'fr';
  const day = parseInt(dayNum);

  useEffect(() => {
    const email = localStorage.getItem("emergence_email");
    if (email) setUserEmail(email);
    fetchDayContent();
  }, [parcoursId, dayNum]);

  const fetchDayContent = async () => {
    try {
      const response = await axios.get(`${API}/emergence/parcours/${parcoursId}/day/${day}?language=${i18n.language}`);
      setDayContent(response.data);
    } catch (error) {
      console.error("Error fetching day:", error);
      toast.error(isFrench ? "Jour non trouvé" : "Day not found");
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteDay = async () => {
    if (!userEmail) {
      toast.error(isFrench ? "Email requis" : "Email required");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/emergence/parcours/complete-day`, {
        email: userEmail,
        parcours_id: parcoursId,
        day: day,
        journaling_response: journalingResponse,
        action_done: actionDone
      });

      toast.success(response.data.message);

      if (response.data.status === "completed") {
        navigate("/emergence/parcours");
      } else {
        navigate(`/emergence/parcours/${parcoursId}/day/${response.data.next_day}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || (isFrench ? "Erreur" : "Error"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="emergence-bg min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#3B7A8C] animate-spin" />
      </div>
    );
  }

  if (!dayContent) {
    return (
      <div className="emergence-bg min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#7A8B99] mb-4">{isFrench ? "Contenu non trouvé" : "Content not found"}</p>
          <Link to="/emergence/parcours">
            <Button className="emergence-btn-primary">{isFrench ? "Retour aux parcours" : "Back to journeys"}</Button>
          </Link>
        </div>
      </div>
    );
  }

  const progress = (day / dayContent.total_days) * 100;

  return (
    <div className="emergence-bg min-h-screen">
      {/* Header */}
      <header className="emergence-glass-header fixed top-0 left-0 right-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-3">
            <Link to="/emergence/parcours" className="flex items-center gap-2 text-[#3B7A8C] hover:opacity-80">
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">{isFrench ? "Parcours" : "Journey"}</span>
            </Link>
            <div className="flex items-center gap-2">
              <Leaf className="w-5 h-5 text-[#3B7A8C]" />
              <span className="font-heading text-sm text-[#2D5A66]">{dayContent.parcours_title}</span>
            </div>
            <span className="text-sm text-[#7A8B99]">
              {isFrench ? `Jour ${day}/${dayContent.total_days}` : `Day ${day}/${dayContent.total_days}`}
            </span>
          </div>
          <div className="emergence-progress">
            <div className="emergence-progress-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="pt-32 pb-24 px-6">
        <div className="max-w-2xl mx-auto">
          {/* Day theme */}
          <div className="text-center mb-8 emergence-animate-in">
            <span className="emergence-badge mb-4 inline-block">
              {isFrench ? `Jour ${day}` : `Day ${day}`}
            </span>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#2D5A66]">
              {dayContent.theme}
            </h1>
          </div>

          {/* Exercise */}
          <Card className="emergence-card border-0 mb-6" style={{ animationDelay: "0.1s" }}>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#3B7A8C]/10 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-[#3B7A8C]" />
                </div>
                <h2 className="font-heading text-lg font-semibold text-[#2D5A66]">
                  {isFrench ? "Réflexion du jour" : "Today's reflection"}
                </h2>
              </div>
              <p className="text-[#3D4F5F] leading-relaxed">
                {dayContent.exercise}
              </p>
            </CardContent>
          </Card>

          {/* Journaling */}
          <Card className="emergence-card border-0 mb-6" style={{ animationDelay: "0.2s" }}>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#D4A574]/10 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-[#D4A574]" />
                </div>
                <h2 className="font-heading text-lg font-semibold text-[#2D5A66]">
                  {isFrench ? "Question de journaling" : "Journaling question"}
                </h2>
              </div>
              <p className="text-[#3D4F5F] mb-4 italic">
                "{dayContent.journaling}"
              </p>
              <Textarea
                value={journalingResponse}
                onChange={(e) => setJournalingResponse(e.target.value)}
                placeholder={isFrench ? "Écris ta réponse ici..." : "Write your response here..."}
                className="min-h-[150px] rounded-xl border-2 border-[#3B7A8C]/20 focus:border-[#3B7A8C] resize-none"
                data-testid="journaling-textarea"
              />
            </CardContent>
          </Card>

          {/* Micro-action */}
          <Card className="emergence-card border-0 mb-8" style={{ animationDelay: "0.3s" }}>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#8FB8A8]/10 flex items-center justify-center">
                  <Target className="w-5 h-5 text-[#8FB8A8]" />
                </div>
                <h2 className="font-heading text-lg font-semibold text-[#2D5A66]">
                  {isFrench ? "Micro-action du jour" : "Today's micro-action"}
                </h2>
              </div>
              <p className="text-[#3D4F5F] leading-relaxed mb-4">
                {dayContent.action}
              </p>
              <button
                onClick={() => setActionDone(!actionDone)}
                className={`flex items-center gap-3 w-full p-4 rounded-xl border-2 transition-all ${
                  actionDone 
                    ? 'border-[#8FB8A8] bg-[#8FB8A8]/10' 
                    : 'border-[#3B7A8C]/20 hover:border-[#3B7A8C]/40'
                }`}
                data-testid="action-checkbox"
              >
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  actionDone ? 'border-[#8FB8A8] bg-[#8FB8A8]' : 'border-[#7A8B99]/40'
                }`}>
                  {actionDone && <CheckCircle className="w-4 h-4 text-white" />}
                </div>
                <span className={`${actionDone ? 'text-[#8FB8A8]' : 'text-[#7A8B99]'}`}>
                  {actionDone 
                    ? (isFrench ? "Action accomplie ! ✨" : "Action done! ✨")
                    : (isFrench ? "Marquer comme fait" : "Mark as done")}
                </span>
              </button>
            </CardContent>
          </Card>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            {day > 1 ? (
              <Button
                variant="outline"
                onClick={() => navigate(`/emergence/parcours/${parcoursId}/day/${day - 1}`)}
                className="rounded-full px-6 border-[#3B7A8C]/20 text-[#7A8B99]"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {isFrench ? `Jour ${day - 1}` : `Day ${day - 1}`}
              </Button>
            ) : (
              <div />
            )}

            <Button
              onClick={handleCompleteDay}
              disabled={saving}
              className="emergence-btn-accent"
              data-testid="complete-day-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              {day === dayContent.total_days 
                ? (isFrench ? "Terminer le parcours" : "Complete journey")
                : (isFrench ? "Valider et continuer" : "Validate and continue")}
              {!saving && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
          </div>

          {/* Encouragement */}
          <p className="text-center text-[#7A8B99] text-sm mt-8">
            {isFrench 
              ? "💚 Prends le temps qu'il te faut. Chaque pas compte."
              : "💚 Take all the time you need. Every step counts."}
          </p>
        </div>
      </main>
    </div>
  );
}
